"use client";

import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { CartContent } from "@/features/cart/ui";
import { getCartTotalWithGiftSets } from "@/features/gift-sets/shared/cartPricing";
import { useGiftSets } from "@/features/gift-sets/ui";
import { useRouter } from "@/i18n/navigation";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { NotFound } from "@/lib/client/components/NotFound";
import { useCountryStore, useShoppingCartStore } from "@/lib/client/stores";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, HStack, Text } from "@/ui";

/**
 * ShoppingCart component - Shopping cart dialog
 */
const ShoppingCart = () => {
  const shoppingCartT = useTranslations("shopping_cart");
  const { isOpen, setIsOpen, cartItems } = useShoppingCartStore();
  const { country } = useCountryStore();
  const giftSets = useGiftSets();
  const router = useRouter();

  const cartTotalForAnalytics = useMemo(
    () => getCartTotalWithGiftSets(cartItems, country.exchangeRate, giftSets),
    [cartItems, country.exchangeRate, giftSets],
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      trackEvent(AnalyticsEvents.CART_CLOSED);
      setIsOpen(false);
    } else {
      trackEvent(AnalyticsEvents.CART_OPENED, {
        item_count: cartItems.length,
        total_value: cartTotalForAnalytics,
      });
    }
  };

  const handleCheckout = () => {
    // Build GA4 items array
    const ga4Items = cartItems.map((item) => {
      const productVariant = item.product.variants.find((v) => v.variant.id === item.variant.id);
      return {
        item_id: item.product.sku || item.product.id,
        item_name: item.product.name,
        item_brand: "TUTULY",
        item_variant: item.variant.variant,
        price: productVariant?.onSalePrice || productVariant?.price || 0,
        quantity: item.quantity,
      };
    });

    trackEvent(AnalyticsEvents.CART_CHECKOUT_CLICKED, {
      item_count: cartItems.length,
      subtotal: cartTotalForAnalytics,
      value: cartTotalForAnalytics,
      currency: country.currencyCode,
      items: ga4Items,
    });
    router.push("/checkout");
    setIsOpen(false);
  };

  const handleContinueShopping = () => {
    trackEvent(AnalyticsEvents.CART_CONTINUE_SHOPPING);
    setIsOpen(false);
  };

  return (
    <Dialog
      modal={false}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <DialogContent
        className="flex max-h-[90vh] min-h-0 max-w-2xl flex-col overflow-hidden"
        data-testid="shopping-cart-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>
            <Text className="text-lg font-medium">{shoppingCartT("title")}</Text>
          </DialogTitle>
        </DialogHeader>
        {cartItems.length > 0 ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <CartContent />
            </div>
            <HStack className="shrink-0 justify-between border-t border-border pt-4">
              <Button
                variant="outline"
                onClick={handleContinueShopping}
              >
                {shoppingCartT("continue_shopping")}
              </Button>
              <Button
                variant="default"
                onClick={handleCheckout}
                disabled={cartItems.length === 0}
              >
                {shoppingCartT("checkout")}
              </Button>
            </HStack>
          </>
        ) : (
          <NotFound
            title={shoppingCartT("no_product")}
            description={shoppingCartT("empty")}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShoppingCart;
